import { useFetchFacets, type FetchKey } from '@app/src/api/fetchHooks'
import type { FacetOption } from '@app/src/pages/ResourceCollection/MrtCollection/mrtCollectionTypes'
import { mapFacetItemsToOptions } from '@app/src/pages/ResourceCollection/MrtCollection/mrtMapping'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import { ONE_MINUTE_MS, spendClassificationOrder } from '@app/src/wf-constants'
import { useMemo } from 'react'
import type { SpendFilterFields } from './spendFilterValue'

export type SpendFilterOptions = {
  periods: FacetOption[]
  spendClassifications: FacetOption[]
  /** The period the editor opens on, since a selection is always about one. */
  defaultPeriodName?: string
  // Whether any row reports a spend period at all. Stated separately from `periods`, which also carries
  // the focused period, so "nothing to filter" stays a question a table can ask rather than something
  // read off an empty list.
  hasReportedPeriods: boolean
}

export type SpendFilterOptionsArgs = {
  endpoint: string
  // Keep both the key and the fields module-level in the caller: they feed the query key, and a fresh
  // array each render would rebuild it every time.
  queryKey: [FetchKey, string]
  fields: SpendFilterFields
  enabled?: boolean
  /** The organization's focused period, offered first and preselected even when no row reports it yet. */
  focusPeriodName?: string
}

export const NO_SPEND_OPTIONS: SpendFilterOptions = {
  periods: [],
  spendClassifications: [],
  hasReportedPeriods: false,
}

/**
 * The spend editor's own option lists. The generic per-column facet fetch serves one facet per column,
 * and this column's filter holds two fields, so it fetches them itself, from the collection's own
 * endpoint and in the namespace the filter uses.
 *
 * Neither list is alphabetical: periods read newest first and intervals follow the band order, which is
 * why they cannot go through useColumnFilterOptions (it sorts alphabetically and takes no comparator).
 */
const useSpendFilterOptions = ({
  endpoint,
  queryKey,
  fields,
  enabled = true,
  focusPeriodName,
}: SpendFilterOptionsArgs): SpendFilterOptions => {
  const facetsParam = useMemo(
    () => [
      { name: fields.periodField, isEnum: true },
      { name: fields.classificationField, isEnum: true },
    ],
    [fields.periodField, fields.classificationField],
  )

  const { facets } = useFetchFacets({
    key: queryKey,
    endpoint,
    facetsParam,
    // The lists are periods and a closed set of bands, so they go stale about as often as a fiscal year.
    options: { enabled, staleTime: ONE_MINUTE_MS },
  })

  // Depend on the facet items rather than the wrapping array, which useFetchFacets rebuilds on every
  // render - memoising on it would hand the column registry a new object each time, and rebuilding the
  // columns changes the derived include set and so the collection's own query key.
  const periodItems = facets?.[0]
  const spendClassificationItems = facets?.[1]

  return useMemo(() => {
    const facetPeriods = mapFacetItemsToOptions(periodItems ?? [])
    // The focused period has to be selectable in the year before anyone has reported spend for it - but
    // never on its own: with no reported period the collection has no spend to filter at all.
    const periods =
      focusPeriodName && facetPeriods.length && !facetPeriods.some(period => period.value === focusPeriodName)
        ? [...facetPeriods, { value: focusPeriodName, label: focusPeriodName }]
        : facetPeriods

    // comparePeriods orders oldest first, so reverse it: the latest period is the one people want.
    const orderedPeriods = [...periods].sort((a, b) => comparePeriods(b.label, a.label))

    return {
      periods: orderedPeriods,
      spendClassifications: mapFacetItemsToOptions(spendClassificationItems ?? []).sort(
        (a, b) => spendClassificationOrder.indexOf(a.label) - spendClassificationOrder.indexOf(b.label),
      ),
      defaultPeriodName: focusPeriodName ?? orderedPeriods[0]?.value,
      hasReportedPeriods: facetPeriods.length > 0,
    }
  }, [periodItems, spendClassificationItems, focusPeriodName])
}

export default useSpendFilterOptions
